import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../config/axios";
import { errorHandler } from "../../../helpers/handlers";

const initialState = {
  items: [],
  lastDocId: null,
  hasMore: false,
  counts: {
    ready: 0,
    check: 0,
    new: 0,
    needs_attention: 0,
    byEntityType: {
      programme: 0,
      school: 0,
      scholarship: 0,
    },
    bySchool: [],
  },
  filters: {
    status: "pending",
    tier: "",
    entityType: "",
    schoolId: "",
    coverageMin: null,
    coverageMax: null,
  },
  current: null,
  proposed: null,
  live: null,
  baseline: null,
  baselineId: null,
  baselineIsDraft: false,
  schoolGate: null,
  pageLoading: false,
  detailLoading: false,
  actionLoading: false,
  bulkLoading: false,
  error: null,
};

const slice = createSlice({
  name: "review",
  initialState,
  reducers: {
    clearReviewDetail: (state) => {
      state.current = null;
      state.proposed = null;
      state.live = null;
      state.baseline = null;
      state.baselineId = null;
      state.baselineIsDraft = false;
      state.schoolGate = null;
    },
    setReviewFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReviewCounts.fulfilled, (state, action) => {
        const payload = action.payload || {};
        state.counts = {
          ...state.counts,
          ...payload,
          byEntityType: {
            ...state.counts.byEntityType,
            ...(payload.byEntityType || {}),
          },
          bySchool: Array.isArray(payload.bySchool)
            ? payload.bySchool
            : state.counts.bySchool || [],
        };
      })
      .addCase(fetchReviewQueue.pending, (state, action) => {
        state.pageLoading = true;
        state.error = null;
        if (!action.meta.arg?.lastDocId) {
          state.items = [];
          state.lastDocId = null;
          state.hasMore = false;
        }
      })
      .addCase(fetchReviewQueue.fulfilled, (state, action) => {
        const { items, lastDocId, reset } = action.payload;
        if (reset) state.items = [];
        const incoming = items || [];
        state.items = [
          ...state.items.filter(
            (existing) => !incoming.some((item) => item.id === existing.id)
          ),
          ...incoming,
        ];
        state.lastDocId = lastDocId || null;
        state.hasMore =
          typeof action.payload.hasMore === "boolean"
            ? action.payload.hasMore
            : incoming.length >= 25;
        state.pageLoading = false;
      })
      .addCase(fetchReviewQueue.rejected, (state, action) => {
        state.pageLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchChangeSet.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(fetchChangeSet.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.current = action.payload.changeSet;
        state.proposed = action.payload.proposed;
        state.live = action.payload.live;
        state.baseline = action.payload.baseline || action.payload.live || null;
        state.baselineId = action.payload.baselineId || null;
        state.baselineIsDraft = Boolean(action.payload.baselineIsDraft);
        state.schoolGate = action.payload.schoolGate || null;
      })
      .addCase(fetchChangeSet.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.payload;
      })
      .addCase(publishChangeSet.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(publishChangeSet.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.items = state.items.filter((i) => i.id !== action.payload.id);
        state.current = null;
      })
      .addCase(publishChangeSet.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
      })
      .addCase(rejectChangeSet.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(rejectChangeSet.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.items = state.items.filter((i) => i.id !== action.payload.id);
        state.current = null;
      })
      .addCase(rejectChangeSet.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
      })
      .addCase(bulkPublishChangeSets.pending, (state) => {
        state.bulkLoading = true;
        state.error = null;
      })
      .addCase(bulkPublishChangeSets.fulfilled, (state, action) => {
        state.bulkLoading = false;
        const publishedIds = new Set(
          (action.payload.published || []).map((p) => p.id)
        );
        state.items = state.items.filter((i) => !publishedIds.has(i.id));
      })
      .addCase(bulkPublishChangeSets.rejected, (state, action) => {
        state.bulkLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearReviewDetail, setReviewFilters } = slice.actions;

export const fetchReviewCounts = createAsyncThunk(
  "review/counts",
  async ({ status = "pending" } = {}, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/admin/review/counts?status=${encodeURIComponent(status)}`
      );
      return response.data.payload || {};
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchReviewQueue = createAsyncThunk(
  "review/list",
  async (
    {
      status = "pending",
      tier = "",
      entityType = "",
      schoolId = "",
      coverageMin = null,
      coverageMax = null,
      lastDocId = "",
      limit = 25,
    } = {},
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams({ status, limit: String(limit) });
      if (tier) params.set("tier", tier);
      if (entityType) params.set("entityType", entityType);
      if (schoolId) params.set("schoolId", schoolId);
      if (coverageMin != null) params.set("coverageMin", String(coverageMin));
      if (coverageMax != null) params.set("coverageMax", String(coverageMax));
      if (lastDocId) params.set("lastDocId", lastDocId);
      const response = await api.get(`/admin/review?${params.toString()}`);
      const payload = response.data.payload || {};
      return {
        items: payload.items || [],
        lastDocId: payload.lastDocId || null,
        hasMore: payload.hasMore,
        reset: !lastDocId,
      };
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchChangeSet = createAsyncThunk(
  "review/get",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/review/${id}`);
      return response.data.payload || {};
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export const publishChangeSet = createAsyncThunk(
  "review/publish",
  async (
    {
      id,
      acceptedFields,
      edits,
      revision,
      convertBody = true,
      polishBody = false,
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(`/admin/review/${id}/publish`, {
        acceptedFields,
        edits,
        revision,
        convertBody,
        polishBody,
      });
      return { id, ...(response.data.payload || {}) };
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

export const rejectChangeSet = createAsyncThunk(
  "review/reject",
  async ({ id, revision, reason }, { rejectWithValue }) => {
    try {
      await api.post(`/admin/review/${id}/reject`, { revision, reason });
      return { id };
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

export const bulkPublishChangeSets = createAsyncThunk(
  "review/bulkPublish",
  async (
    {
      ids = null,
      tier = "ready",
      entityType = "",
      schoolId = "",
      coverageMin = null,
      coverageMax = null,
      convertBody = true,
      polishBody = false,
      limit = 200,
    } = {},
    { rejectWithValue }
  ) => {
    try {
      const body = { convertBody, polishBody, limit };
      if (Array.isArray(ids) && ids.length) {
        body.ids = ids;
      } else {
        body.tier = tier || "ready";
        if (entityType) body.entityType = entityType;
        if (schoolId) body.schoolId = schoolId;
        if (coverageMin != null) body.coverageMin = coverageMin;
        if (coverageMax != null) body.coverageMax = coverageMax;
      }
      const response = await api.post(`/admin/review/bulk-publish`, body);
      return response.data.payload || { published: [], failed: [] };
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.errors?.global ||
          error.message
      );
    }
  }
);

export default slice.reducer;
